import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogoIcon } from "../components/Brand";
import { useApp } from "../AppContext";
import { ArrowRight, KeyRound, Sun, Moon } from "lucide-react";
import { useTheme } from "../hooks/useTheme";
import ProgereLegalFooter from "../components/ProgereLegalFooter";

/**
 * Public client portal landing — clients enter their unique quote share code
 * to view/sign a quote they received by email.
 */
export default function ClientPortal() {
  const { lang, setLang } = useApp();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const isFr = lang === "fr";
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    const token = code.trim();
    if (!token) return setErr(isFr ? "Veuillez saisir votre code d'accès." : "Please enter your access code.");
    nav(`/quote/share/${encodeURIComponent(token)}`);
  };

  return (
    <div className="min-h-screen flex flex-col relative"
      style={{ background: "radial-gradient(ellipse at top, rgba(212,175,55,0.08) 0%, transparent 55%), radial-gradient(ellipse at bottom, rgba(192,0,0,0.08) 0%, transparent 55%), #0B0B0B" }}>
      <div className="absolute top-5 right-5 flex items-center gap-2 z-10">
        <button onClick={() => setLang(isFr ? "en" : "fr")}
          className="text-xs px-3 py-1.5 rounded-full transition hover:brightness-110"
          style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)", color: "rgba(245,245,245,0.85)" }}
          data-testid="portal-lang-toggle">
          {isFr ? "EN" : "FR"}
        </button>
        <button onClick={toggleTheme}
          className="w-9 h-9 rounded-full flex items-center justify-center transition hover:brightness-110"
          style={{ background: "rgba(245,245,245,0.06)", border: "1px solid rgba(245,245,245,0.1)" }}
          aria-label="Toggle theme" data-testid="portal-theme-toggle">
          {theme === "dark" ? <Sun size={14} color="#D4AF37" /> : <Moon size={14} color="#0B0B0B" />}
        </button>
      </div>

      <div className="flex-1 w-full flex items-center justify-center px-4 sm:px-6 py-10">
        <div className="w-full max-w-md rounded-3xl p-8 sm:p-10"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.9) 0%, rgba(11,11,11,0.95) 100%)",
            border: "1px solid rgba(212,175,55,0.18)",
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.03)",
          }}
          data-testid="client-portal">
          <div className="flex flex-col items-center mb-6">
            <div className="mb-4"><LogoIcon size="lg" /></div>
            <div className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-center leading-none">
              <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
              <span className="ml-2" style={{ color: "#F5F5F5" }}>DEVIS AI</span>
            </div>
          </div>
          <div className="text-center mb-6">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: "#D4AF37" }}>
              {isFr ? "Espace client" : "Client portal"}
            </div>
            <p className="text-sm" style={{ color: "rgba(245,245,245,0.65)" }}>
              {isFr
                ? "Saisissez le code d'accès unique reçu par courriel pour consulter votre soumission."
                : "Enter the unique access code you received by email to view your quote."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "rgba(245,245,245,0.7)" }}>
              {isFr ? "CODE D'ACCÈS" : "ACCESS CODE"}
            </label>
            <div className="relative">
              <KeyRound size={15} className="absolute left-4 top-1/2 -translate-y-1/2" color="rgba(212,175,55,0.6)" />
              <input
                type="text"
                className="input-dark pl-11 tracking-wider font-mono"
                placeholder="XXXX-XXXX-XXXX"
                value={code} onChange={(e) => setCode(e.target.value)}
                required autoFocus data-testid="portal-code" />
            </div>
            {err && (
              <div className="text-sm px-3 py-2.5 rounded-xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }} data-testid="portal-error">
                {err}
              </div>
            )}
            <button type="submit" className="btn-gold w-full mt-2" data-testid="portal-submit">
              <ArrowRight size={16} /> {isFr ? "Accéder à ma soumission" : "Access my quote"}
            </button>
          </form>

          <div className="mt-6 pt-4 text-center text-xs border-t" style={{ borderColor: "rgba(245,245,245,0.06)" }}>
            <Link to="/login" className="hover:underline" style={{ color: "rgba(212,175,55,0.85)" }} data-testid="portal-back-login">
              ← {isFr ? "Retour à l'espace entrepreneur" : "Back to contractor sign-in"}
            </Link>
          </div>

          <p className="mt-6 text-center text-[11px]" style={{ color: "rgba(245,245,245,0.4)" }}>
            PROGÈRE — {isFr ? "Propulsé par" : "Powered by"}{" "}
            <span style={{ color: "#D4AF37" }}>Groupe MJR Construction Inc.</span>
          </p>
        </div>
      </div>

      <ProgereLegalFooter showLogin={false} />
    </div>
  );
}
